import Hls from 'hls.js';
import { useEffect, useRef, useState } from 'react';

interface HlsPreviewProps {
  /** Public HLS playlist URL (`.m3u8`). Null disables the player. */
  src: string | null;
  className?: string;
  /** Re-attach the source when this changes (e.g. on stream-key swap). */
  resetKey?: string;
  /**
   * When `true`, the `<video>` element unmutes so the operator hears
   * the audio track that's already in the HLS playlist. Defaults to
   * `false` because unmuted autoplay is blocked by browsers without a
   * recent user gesture — the parent should only pass `true` when the
   * operator explicitly toggles audio on, at which point the click IS
   * the gesture and unmuting will be honoured.
   */
  audio?: boolean;
}

/**
 * Tiny HLS player for the green-room preview. Uses hls.js on browsers
 * without native HLS (Chrome, Firefox); falls back to the native
 * `<video>` element on Safari.
 *
 * Deliberately minimal — muted-on-load, autoplays, no controls. The
 * operator uses this to check "is the feed up and looking right"
 * before promoting; if they want to hear the publisher, they toggle
 * audio on via the parent `PreviewPlayer`.
 *
 * **Retry policy**: SRS takes 8-10 s from first publish to the first
 * HLS fragment being served, so an operator who opens the preview
 * card the moment a correspondent goes live will hit an empty
 * playlist. Without retry the component gets stuck on "Preview
 * unavailable" and the only fix is to toggle transports. So we auto-
 * retry with backoff up to `MAX_ATTEMPTS` — enough to cover a fresh
 * publish, a slow encoder, or a brief upstream hiccup, without
 * spamming SRS forever if the source is genuinely dead. Attempts
 * reset when `src` or `resetKey` changes (new stream, new session).
 */
const MAX_ATTEMPTS = 20;
const BACKOFF_BASE_MS = 1500;
const BACKOFF_CAP_MS = 5000;
/** Watchdog: if we don't reach `playing` within this window, count
 *  as an implicit failure and trigger a retry. Covers the case where
 *  hls.js's own retries silently exhaust or a WHEP session sits
 *  in `connecting` forever. */
const STUCK_TIMEOUT_MS = 12_000;

export function HlsPreview({
  src,
  className,
  resetKey,
  audio = false,
}: HlsPreviewProps): JSX.Element | null {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // `blocked` is a distinct state from `error`: the media pipeline is
  // healthy and a frame is ready to render, but the browser refused
  // to autoplay (Firefox especially will reject if the audio toggle
  // has unmuted the element, or if the site hasn't yet received a
  // user gesture). The overlay shows a tap-to-play button, which uses
  // the user's click as the required activation.
  const [status, setStatus] = useState<
    'loading' | 'playing' | 'blocked' | 'error' | 'idle'
  >('idle');
  const [attempts, setAttempts] = useState(0);
  // Internal reset trigger — bumped when we detect a resolution change
  // on the video element (mobile publisher flipped portrait↔landscape
  // mid-stream). SRS keeps serving the old SPS/PPS to already-connected
  // HLS sessions from its per-session cache; the only reliable way to
  // pick up the new codec config is to destroy the current hls.js
  // instance and start a fresh one. Feeding this into the main
  // playback effect's dep array does exactly that.
  const [resolutionEpoch, setResolutionEpoch] = useState(0);

  // Reset attempt counter when src / resetKey changes so a brand-new
  // stream starts with a fresh budget instead of inheriting the tail
  // of the last one. Also reset on `resolutionEpoch` so a mid-stream
  // orientation flip doesn't inherit exhausted retry budget from the
  // previous session.
  useEffect(() => {
    setAttempts(0);
  }, [src, resetKey, resolutionEpoch]);

  useEffect(() => {
    if (!src) {
      setStatus('idle');
      return;
    }
    const video = videoRef.current;
    if (!video) return;

    setStatus('loading');
    let stuckTimer: ReturnType<typeof setTimeout> | undefined;
    const armStuckWatchdog = () => {
      stuckTimer = setTimeout(() => setStatus('error'), STUCK_TIMEOUT_MS);
    };
    const disarmStuckWatchdog = () => {
      if (stuckTimer) clearTimeout(stuckTimer);
      stuckTimer = undefined;
    };

    // Safari supports HLS natively — no hls.js needed and it has
    // better hardware decoding.
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
      // Autoplay may be rejected: Firefox especially blocks even
      // muted autoplay on some pages if the site hasn't been
      // "activated" by a user gesture yet. `NotAllowedError` is the
      // documented DOM signal for that. Flip to `blocked` so the
      // overlay surfaces a tap-to-play button; other rejection kinds
      // (rare) are swallowed and the media element will surface
      // errors via its `error` event.
      video.play().catch((err) => {
        if (err?.name === 'NotAllowedError') {
          disarmStuckWatchdog();
          setStatus('blocked');
        }
      });
      const onPlaying = () => {
        disarmStuckWatchdog();
        setStatus('playing');
      };
      const onError = () => setStatus('error');
      video.addEventListener('playing', onPlaying);
      video.addEventListener('error', onError);
      armStuckWatchdog();
      return () => {
        disarmStuckWatchdog();
        video.removeEventListener('playing', onPlaying);
        video.removeEventListener('error', onError);
        video.removeAttribute('src');
        video.load();
      };
    }

    if (!Hls.isSupported()) {
      setStatus('error');
      return;
    }

    // hls.js path — everything except Safari. Bumped internal retries
    // so hls.js quietly handles the "SRS just started publishing"
    // window (first fragment ~8-10 s after publish). Anything hls.js
    // itself can't recover from will bubble as `fatal`, which our
    // outer retry loop catches.
    const hls = new Hls({
      // `lowLatencyMode: false` — deliberately OFF even though the
      // name is tempting. LL-HLS is a specific protocol variant
      // requiring the server to emit `#EXT-X-PART` tags and support
      // blocking playlist reload with `_HLS_msn`/`_HLS_part` query
      // params. SRS 5 doesn't implement any of that. When lowLatencyMode
      // was on, hls.js kept polling for partial fragments that don't
      // exist AND held its buffer targets so tight that any small
      // playlist-refresh delay caused a stall.
      lowLatencyMode: false,
      // Live-edge tuning. Started at `liveSyncDuration: 6` which put
      // the playhead ~4 s inside the 10 s server window — thin cushion
      // that stalled on any transient jitter. Widened to 10 s to give
      // 4 fragments of buffer between the playhead and the live edge,
      // so a slow fragment fetch has time to complete before the
      // decoder starves. Adds latency (~4 s more end-to-end) but the
      // stall trade-off is worth it — HLS is the "audio-check" path,
      // not the sub-second video preview (that's WebRTC).
      liveSyncDuration: 10,
      liveMaxLatencyDuration: 20,
      // 60 s of forward buffer so hls.js can accumulate a large
      // cushion when the network's fast and burn through it during
      // slow-fetch stretches. Server `hls_window` caps how much
      // history is available anyway, so we won't over-buffer; this
      // just enables buffering to the max the server can provide.
      maxBufferLength: 60,
      maxMaxBufferLength: 120,
      backBufferLength: 30,
      // Stall recovery — how hls.js reacts when it detects the
      // playhead isn't advancing despite having decoded frames
      // available. Defaults (`nudgeMaxRetry: 3`, `nudgeOffset: 0.1`)
      // are too timid: hls.js gives up nudging after ~0.3 s of stall
      // and falls back to a costly buffer flush. Cranking these up
      // (10 attempts × 0.3 s = ~3 s of gentle nudging before flush)
      // recovers from short PTS gaps and micro-stalls smoothly.
      nudgeMaxRetry: 10,
      nudgeOffset: 0.3,
      // Watchdog cadence — how often hls.js checks whether the
      // playback is progressing when we have buffered content ahead.
      // Default is 3 s; tightened to 2 s so a stall is detected +
      // nudged within ~2 s rather than up to 3 s.
      highBufferWatchdogPeriod: 2,
      // Playlist + fragment retries. Original defaults are too tight
      // for the "SRS still writing the first fragment" cold-start
      // window; bumped so the outer retry loop only kicks in when
      // hls.js has truly given up.
      manifestLoadingMaxRetry: 8,
      manifestLoadingRetryDelay: 1000,
      levelLoadingMaxRetry: 8,
      levelLoadingRetryDelay: 1000,
      fragLoadingMaxRetry: 8,
      fragLoadingRetryDelay: 1000,
    });
    hls.loadSource(src);
    hls.attachMedia(video);
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      // See the Safari branch above for the autoplay-policy
      // rationale. `NotAllowedError` → surface tap-to-play; other
      // rejections are swallowed and any real failure will surface
      // via the video element's `error` event.
      video.play().catch((err) => {
        if (err?.name === 'NotAllowedError') {
          disarmStuckWatchdog();
          setStatus('blocked');
        }
      });
    });
    // Error handling follows hls.js's own recommended pattern: try
    // in-place recovery first (which usually works — hls.js can
    // resume from a stalled buffer or swap decoder context without
    // needing a full teardown), and only fall through to the outer
    // retry loop when in-place recovery fails too.
    //
    // Before this, ANY fatal error immediately set `status = 'error'`
    // which destroyed the hls.js instance via the effect cleanup. The
    // outer retry then created a fresh instance, played one keyframe,
    // hit the same error, and looped forever — that's the "one
    // frame → reconnecting → one frame → reconnecting" pattern you
    // see when a mid-stream transient network hiccup gets treated as
    // "the whole session is dead."
    let recoveredOnce = false;
    hls.on(Hls.Events.ERROR, (_evt, data) => {
      if (!data.fatal) return;
      // hls.js emits NETWORK_ERROR for playlist/fragment fetch
      // failures and MEDIA_ERROR for MSE append / decode issues.
      // Both have documented in-place recovery paths; either succeeds
      // silently or emits ANOTHER fatal error we then fall through on.
      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          if (!recoveredOnce) {
            recoveredOnce = true;
            hls.startLoad();
            return;
          }
          break;
        case Hls.ErrorTypes.MEDIA_ERROR:
          if (!recoveredOnce) {
            recoveredOnce = true;
            hls.recoverMediaError();
            return;
          }
          break;
      }
      // Second fatal of the same session, or an OTHER_ERROR type
      // (unrecoverable). Give up; the outer loop will reconnect
      // from scratch with backoff.
      setStatus('error');
    });
    const onPlaying = () => {
      disarmStuckWatchdog();
      setStatus('playing');
    };
    video.addEventListener('playing', onPlaying);
    armStuckWatchdog();

    return () => {
      disarmStuckWatchdog();
      video.removeEventListener('playing', onPlaying);
      hls.destroy();
    };
  }, [src, resetKey, attempts, resolutionEpoch]);

  // Detect mid-stream resolution changes and force a fresh hls.js
  // instance. Root cause: when the mobile publisher flips orientation
  // (portrait ↔ landscape) mid-broadcast, the encoder rebuilds at the
  // new dimensions and pushes new-shape frames to SRS. SRS forwards
  // them, but its HLS handler keeps serving the ORIGINAL SPS/PPS in
  // the playlist header until either the session times out (~60 s of
  // idle after full disconnect) or a subscriber initiates a fresh
  // session. Meanwhile, our long-lived hls.js instance's MSE
  // SourceBuffer keeps decoding new fragments against the old codec
  // config — it either shows the old canvas dimensions with the new
  // content rotated inside, or silently stalls.
  //
  // The `resize` event on `<video>` fires when the successfully
  // decoded frame's intrinsic dimensions change. When it fires
  // *during* playback (not on initial source load), we treat it as
  // "the incoming stream shape changed" and bump `resolutionEpoch`,
  // which restarts the main effect above with a fresh hls.js — that
  // subscriber initiates a new HLS session that SRS treats as fresh
  // and starts serving the correct SPS/PPS to. Guards:
  //   • ignore the very first resize (that's the initial 0×0→WxH
  //     transition as the first frame is decoded);
  //   • require `status === 'playing'` (we've made it past the
  //     initial load) before treating a resize as a real change.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let lastW = video.videoWidth;
    let lastH = video.videoHeight;
    const onResize = () => {
      const w = video.videoWidth;
      const h = video.videoHeight;
      // First real dimensions after `loading` → just record.
      if (lastW === 0 || lastH === 0) {
        lastW = w;
        lastH = h;
        return;
      }
      if (w === 0 || h === 0) return;
      if (w === lastW && h === lastH) return;
      lastW = w;
      lastH = h;
      // Real mid-stream shape change. Force a fresh hls.js.
      setResolutionEpoch((n) => n + 1);
    };
    video.addEventListener('resize', onResize);
    return () => video.removeEventListener('resize', onResize);
  }, [src, resetKey]);

  // Retry on error with capped exponential backoff. Only fires when
  // status is `error` AND we still have attempts left — otherwise the
  // component sticks at "unavailable" so the operator can see the
  // preview is genuinely dead, not just slow.
  useEffect(() => {
    if (status !== 'error' || attempts >= MAX_ATTEMPTS) return;
    const backoff = Math.min(BACKOFF_BASE_MS * Math.pow(1.35, attempts), BACKOFF_CAP_MS);
    const t = setTimeout(() => setAttempts((a) => a + 1), backoff);
    return () => clearTimeout(t);
  }, [status, attempts]);

  if (!src) return null;

  return (
    <div className={`relative overflow-hidden rounded-md bg-black ${className ?? ''}`}>
      <video
        ref={videoRef}
        className="aspect-video w-full"
        // See `audio` prop comment. Muted until the operator opts in.
        muted={!audio}
        playsInline
        autoPlay
      />
      {status === 'blocked' && (
        // Tap-to-play. The click provides the user gesture the
        // browser was waiting for; play() called synchronously in the
        // click handler will succeed and the `playing` event flips
        // status. Full-overlay button so any tap on the card counts —
        // an operator scanning cards shouldn't have to aim for a
        // small button. `NotAllowedError` on the follow-up call (rare
        // after a gesture) drops to `error` so the outer retry loop
        // takes over.
        <button
          type="button"
          onClick={() => {
            videoRef.current?.play().catch((err) => {
              if (err?.name !== 'NotAllowedError') {
                setStatus('error');
              }
            });
          }}
          className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/70 text-white transition-colors hover:bg-black/60"
          aria-label="Tap to play preview"
        >
          <span className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-white/90">
            <span className="ml-1 text-2xl leading-none">▶</span>
          </span>
          <span className="text-xs font-medium uppercase tracking-wider">
            Tap to play
          </span>
        </button>
      )}
      {status !== 'playing' && status !== 'blocked' && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/60 text-xs text-white/80">
          {status === 'loading' && 'Connecting preview…'}
          {status === 'error' && (attempts >= MAX_ATTEMPTS ? 'Preview unavailable' : 'Reconnecting…')}
          {status === 'idle' && ' '}
        </div>
      )}
    </div>
  );
}
